"use client";
import { Header } from '@/pages/home/sections/header';
import { Footer } from '@/pages/home/sections/footer';
import { motion } from 'framer-motion';
import { StandardCTA } from '@/components/standard-cta';
import { BookOpen, Users, Globe, CheckCircle, Heart } from 'lucide-react';

// Simple local data blocks
const stats = [
  { label: 'Families Served', value: '2,300+' },
  { label: 'Certified Instructors', value: '45+' },
  { label: 'Global Regions', value: '28' },
  { label: 'Learner Rating', value: '4.9/5' }
];

const values = [
  { title: 'Authentic Transmission', desc: 'Guided by ijazah-certified scholars so every tajwīd, tafsīr and fiqh lesson is rooted in trusted tradition.' },
  { title: 'Instructional Excellence', desc: 'Inspired by leading digital academies, lessons blend micro-learning, feedback loops and measurable milestones.' },
  { title: 'Personalized Mastery', desc: 'Adaptive pathways align memorisation, Arabic fluency and Islamic literacy with each learner’s pace.' },
  { title: 'Character & Purpose', desc: 'We cultivate adab, resilience and intention so knowledge transforms into lived worship and service.' }
];

const timeline = [
  { year: '2021', title: 'Digital Qur’an Circle', text: 'Launched Dhikr Institute with small-group tajwīd coaching mirroring traditional halaqāt inside a modern virtual classroom.' },
  { year: '2022', title: 'Curriculum Verticalisation', text: 'Rolled out Arabic language ladders, Islamic Studies modules and data-backed learning plans referencing best practices from global academies.' },
  { year: '2023', title: 'Global Instructor Network', text: 'Onboarded veteran male and female teachers across time zones, offering live sessions that accommodate families worldwide.' },
  { year: '2024', title: 'Learning Intelligence', text: 'Deployed analytics dashboards, memorisation trackers and reflective journaling to keep students accountable and motivated.' },
  { year: '2025', title: 'Impact Scaling', text: 'Expanding AI-assisted recitation review, on-demand Arabic labs and service-learning tracks for a holistic ummah-building experience.' }
];

export default function AboutPage(){
  return (
    <div className="min-h-screen bg-[#FCF8F1]">
      <Header />
      <main>
        {/* Hero */}
        <section className="pt-32 pb-16 sm:pt-40 sm:pb-20 lg:pt-48 lg:pb-24 bg-[#FCF8F1]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl">
              <div className="inline-flex items-center px-3 py-1 text-sm font-medium text-[#245E51] bg-[#A8FF51]/20 rounded-full border border-[#A8FF51]/30">
                About Us
              </div>
              <motion.h1 initial={{ opacity:0, y:28 }} animate={{ opacity:1, y:0 }} transition={{ duration:.75, ease:[0.23,0.68,0.32,0.97] }} className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight text-gray-900">
                Your Trusted Online <span className="text-transparent bg-gradient-to-r from-[#245E51] to-[#A8FF51] bg-clip-text">Qur’an & Arabic Academy</span>
              </motion.h1>
              <motion.p initial={{ opacity:0, y:30 }} animate={{ opacity:1, y:0 }} transition={{ duration:.8, delay:.06, ease:[0.23,0.68,0.32,0.97] }} className="mt-6 text-lg sm:text-xl text-gray-600 max-w-2xl leading-relaxed">
                Dhikr Institute combines <strong>Egyptian certified Quran teachers</strong> with <strong>UK professional management</strong> to deliver world-class Islamic education online. Our programs are suitable for <strong>all ages and genders</strong> around the world.
              </motion.p>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="w-[90%] max-w-7xl mx-auto -mt-10 relative z-10">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((s,i)=> (
              <motion.div key={s.label} initial={{ opacity:0, y:20 }} whileInView={{ opacity:1, y:0 }} viewport={{ once:true }} transition={{ duration:.55, delay:i*0.05, ease:[0.23,0.68,0.32,0.97] }} className="rounded-2xl bg-white/80 backdrop-blur-xl border border-white/50 shadow-xl px-6 py-8 flex flex-col items-start">
                <span className="text-3xl font-bold bg-gradient-to-r from-[#245E51] to-[#A8FF51] text-transparent bg-clip-text tracking-tight">{s.value}</span>
                <span className="mt-2 text-sm font-semibold uppercase tracking-wide text-gray-700">{s.label}</span>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Mission + Vision */}
        <section className="py-28">
          <div className="w-[90%] max-w-7xl mx-auto grid lg:grid-cols-2 gap-20 items-start">
            <motion.div initial={{ opacity:0, y:30 }} whileInView={{ opacity:1, y:0 }} viewport={{ once:true, amount:.4 }} transition={{ duration:.7, ease:[0.23,0.68,0.32,0.97] }} className="space-y-8">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">Our Mission</h2>
              <p className="text-gray-600 leading-relaxed text-lg max-w-xl">To nurture confident reciters, Arabic speakers and conscious Muslims through structured curricula, soulful mentorship and intelligent technology that keeps progress visible.</p>
              <div className="grid sm:grid-cols-2 gap-6 pt-4">
                {values.map(v => (
                  <div key={v.title} className="rounded-2xl bg-white border border-gray-100 shadow-md hover:shadow-lg transition-all p-6 group">
                    <h3 className="font-semibold text-gray-900 tracking-tight mb-2 group-hover:text-[#245E51]">{v.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{v.desc}</p>
                  </div>
                ))}
              </div>
            </motion.div>
            <motion.div initial={{ opacity:0, y:40 }} whileInView={{ opacity:1, y:0 }} viewport={{ once:true, amount:.4 }} transition={{ duration:.8, ease:[0.23,0.68,0.32,0.97] }} className="relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl border border-white/40 bg-gradient-to-br from-[#245E51] to-[#0f362d] p-10 text-white">
                <div className="max-w-md space-y-6">
                  <h3 className="text-2xl font-bold">A Learning Experience Anchored in Impact</h3>
                  <p className="text-white/85 leading-relaxed text-sm sm:text-base">From tajwīd clinics to Arabic morphology labs and faith-driven coaching, every touchpoint is intentionally designed to build mastery, mindset and meaningful application.</p>
                  <ul className="space-y-3 text-sm text-white/80">
                    <li className="flex items-start gap-3"><span className="w-2 h-2 rounded-full bg-[#A8FF51] mt-2" /> Guided memorization pathways</li>
                    <li className="flex items-start gap-3"><span className="w-2 h-2 rounded-full bg-[#A8FF51] mt-2" /> Recitation feedback & mastery checkpoints</li>
                    <li className="flex items-start gap-3"><span className="w-2 h-2 rounded-full bg-[#A8FF51] mt-2" /> Multi-track Arabic progression</li>
                    <li className="flex items-start gap-3"><span className="w-2 h-2 rounded-full bg-[#A8FF51] mt-2" /> Structured Islamic Studies foundations</li>
                    <li className="flex items-start gap-3"><span className="w-2 h-2 rounded-full bg-[#A8FF51] mt-2" /> Mentorship & consistency systems</li>
                  </ul>
                </div>
                <div className="absolute -right-24 -top-24 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
                <div className="absolute -left-20 -bottom-20 w-96 h-96 bg-black/10 rounded-full blur-3xl" />
              </div>
            </motion.div>
          </div>
        </section>

        {/* Team Section */}
        <section className="py-24 bg-[#FCF8F1]">
          <div className="max-w-7xl mx-auto px-4">
            <div className="max-w-2xl mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                World-Class Team
              </h2>
              <p className="text-xl text-gray-600">
                Combining authentic scholarship with professional management
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              {/* Egyptian Teachers */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-white to-[#A8FF51]/10 rounded-3xl p-8 shadow-xl border border-gray-100"
              >
                <div className="w-16 h-16 rounded-2xl bg-[#A8FF51]/20 flex items-center justify-center mb-6">
                  <BookOpen className="h-8 w-8 text-[#245E51]" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  Egyptian Certified Teachers
                </h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Teachers with Ijazah in Quran recitation and deep knowledge of Islamic sciences. 
                  They bring authentic scholarship and traditional teaching methods.
                </p>
                <div className="flex items-center gap-2 text-sm font-semibold text-[#245E51]">
                  <Users className="h-4 w-4" />
                  <span>45+ Certified Teachers</span>
                </div>
              </motion.div>
              
              {/* UK Administration */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="bg-gradient-to-br from-white to-[#A8FF51]/10 rounded-3xl p-8 shadow-xl border border-gray-100"
              >
                <div className="w-16 h-16 rounded-2xl bg-[#A8FF51]/20 flex items-center justify-center mb-6">
                  <Globe className="h-8 w-8 text-[#245E51]" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  UK Professional Management
                </h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Professional management ensuring quality, consistency, and excellent customer service. 
                  We combine traditional knowledge with modern standards.
                </p>
                <div className="flex items-center gap-2 text-sm font-semibold text-[#245E51]">
                  <CheckCircle className="h-4 w-4" />
                  <span>International Professional Oversight</span>
                </div>
              </motion.div>
              
              {/* Inclusive */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="bg-gradient-to-br from-white to-[#A8FF51]/10 rounded-3xl p-8 shadow-xl border border-gray-100"
              >
                <div className="w-16 h-16 rounded-2xl bg-[#A8FF51]/20 flex items-center justify-center mb-6">
                  <Heart className="h-8 w-8 text-[#245E51]" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  Inclusive for All
                </h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Programs suitable for all genders and ages from children to adults, 
                  men and women, from different countries around the world.
                </p>
                <div className="flex items-center gap-2 text-sm font-semibold text-[#245E51]">
                  <Globe className="h-4 w-4" />
                  <span>2,300+ families from 28 countries</span>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        <StandardCTA
          badge="Aligned & Purposeful"
          titlePrefix="Start Your"
          titleHighlight="Structured Path"
          titleSuffix=""
          paragraph="Experience mentorship, mastery mapping and transformative Qur’an-centered progress across recitation, Arabic and applied understanding."
          primaryLabel="Explore Programs"
          points={[
            { icon: 'assessment', label: 'Free placement review' },
            { icon: 'plan', label: 'Adaptive study pathways' },
            { icon: 'rating', label: 'High retention outcomes' }
          ]}
        />
      </main>
      <Footer />
    </div>
  );
}
